import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { LAYOUT_COLUMN__left } from '@/pages/_base/layout_base/LAYOUT_COLUMN__left';
import { LAYOUT_COLUMN__right } from '@/pages/_base/layout_base/LAYOUT_COLUMN__right';
import { FOOTER__master } from '@/pages/_base/layout_footer/FOOTER__master';
import { HEADER__master } from '@/pages/_base/layout_header/HEADER__master';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { LOCAL__ui_put_set_of_strings } from '@/state/ui/_base/LOCAL__ui_put_set_of_strings';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { MAP__fields_by_id } from '@shared/types/types';
import { G_SIDEBAR__edges } from '../../sidebar/G_SIDEBAR__edges';
import { G_SIDEBAR__fields } from '../../sidebar/G_SIDEBAR__fields';
import { G_HEADER__column_center } from '../../view_header/G_HEADER__column_center';
import { G_HEADER__generation_actions } from '../../view_header/G_HEADER__generation_actions';
import { G_BAR__layout_tabs } from '../_base/G_BAR__layout_tabs';
import { G_DROPDOWN__generation_tool } from '../_base/graph_template/G_DROPDOWN__generation_tool';
import { G_DROPDOWN__generation_output_type } from '../outputs/sidebar/G_DROPDOWN__generation_output_type';
import { G_SIDEBAR__inputs_filters } from './filters/G_SIDEBAR__inputs_filters';
import { G_TABLE__inputs_master } from './tables/G_TABLE__inputs_master';

// table shared between tools: node type, field alias, value
// fr: button clear value
//fr: button clear alias
//fr: button edit alias

// table used: node type, field path, value

export function G_PAGE_LAYOUT__generation_bundles() {
  const dispatch = use_dispatch();

  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_inputs__fields_ids_selected } = use_slice_ui(['g_inputs__fields_ids_selected']);

  const { graph } = generation_current.tool;

  /*
   *
   */
  use_effect(() => {
    return () => void dispatch(LOCAL__ui_put_set_of_strings({ key: 'g_inputs__fields_ids_selected', value: new Set() }));
  }, []);

  /*
   * METHODS
   */

  function get_fields_sidebar_right() {
    let out: FIELD__base[] = [];

    if (g_inputs__fields_ids_selected.is_empty()) {
      out = graph.fields__ui_visible();

      let fields_visible_map = FIELD__base.map_by_field_id(out);

      try_add_grid2d_row(out, fields_visible_map);
      try_add_grid2d_column(out, fields_visible_map);
    } else {
      let fields_map = graph.fields__get_map();

      for (let field_id of g_inputs__fields_ids_selected) {
        if (!fields_map.has(field_id)) continue;

        let field = fields_map.get_or_assert(field_id);
        if (!field.meta.ui__has_component) continue;

        out.push(field);
      }
    }

    return out;
  }

  function try_add_grid2d_row(target_array: FIELD__base[], fields_visible_map: MAP__fields_by_id) {
    let field_grid2d_row = graph.grid2d__get_field_row();
    let grid2d_row_stub = graph.invocation_grid2d_view.stub_grid2d_row;

    if (field_grid2d_row.meta.field_id == grid2d_row_stub.meta.field_id) return; //no need to add stub
    if (fields_visible_map.has(field_grid2d_row.meta.field_id)) return; //already added
    if (!field_grid2d_row.meta.ui__has_component) return; //skip fields without ui

    target_array.push(field_grid2d_row);
    fields_visible_map.set(field_grid2d_row.meta.field_id, field_grid2d_row);
  }

  function try_add_grid2d_column(target_array: FIELD__base[], fields_visible_map: MAP__fields_by_id) {
    let field_grid2d_column = graph.grid2d__get_field_column();
    let grid2d_column_stub = graph.invocation_grid2d_view.stub_grid2d_column;

    if (field_grid2d_column.meta.field_id == grid2d_column_stub.meta.field_id) return; //no need to add stub
    if (fields_visible_map.has(field_grid2d_column.meta.field_id)) return; //already added
    if (!field_grid2d_column.meta.ui__has_component) return; //skip fields without ui

    target_array.push(field_grid2d_column);
    fields_visible_map.set(field_grid2d_column.meta.field_id, field_grid2d_column);
  }

  function get_edges_sidebar_right() {
    return graph.edges__ui_visible();
  }

  /*
   *
   */

  function render_sidebar_title() {
    let { tool } = generation_current;

    let text = '';

    if (g_inputs__fields_ids_selected.size > 0) {
      text = `Fields selected ${g_inputs__fields_ids_selected.size}`;
    } else {
      let tool_name = tool.grid2d_views_map.size() <= 1 ? tool.name : `${tool.name}→${tool.grid2d_view_current_name}`;

      text = `Fields of tool "${tool_name}"`;
    }

    return <div className="px-2 w-full h-6 flex items-center text-xs text-bar-foreground border-b border-component-border truncate">{text}</div>;
  }

  function render_column_left() {
    return (
      <LAYOUT_COLUMN__left could_be_hidden>
        <div className="flex flex-col size-full overflow-y-auto">
          <G_DROPDOWN__generation_output_type />
          <PLACEHOLDER_bar_line className="w-full h-6 border-b border-component-border" />
          <G_DROPDOWN__generation_tool />

          <G_SIDEBAR__inputs_filters />
        </div>
      </LAYOUT_COLUMN__left>
    );
  }

  function render_column_right() {
    let edges_right = get_edges_sidebar_right();
    let fields_right = get_fields_sidebar_right();

    if (edges_right.is_empty() && fields_right.is_empty()) return null;

    return (
      <LAYOUT_COLUMN__right could_be_hidden>
        <div className="flex flex-col size-full overflow-y-auto">
          {render_sidebar_title()}
          <G_SIDEBAR__fields fields={fields_right} class_name="overflow-y-auto" />
          <G_SIDEBAR__edges title="Edges" edges={edges_right} name_type={'full'} />
        </div>
      </LAYOUT_COLUMN__right>
    );
  }

  /*
   * HTML
   */

  if (generation_current.is_stub()) return null;

  return (
    <div className="flex flex-col w-full h-screen">
      <HEADER__master column_center={<G_HEADER__column_center />} column_right={<G_HEADER__generation_actions />} />
      <div className="flex flex-row w-full flex-1 min-h-0">
        {render_column_left()}

        <div className="relative flex-1 flex flex-col h-full min-h-0 min-w-0">
          <G_BAR__layout_tabs />

          <div className="w-full flex-1 min-h-0 min-w-0">
            <G_TABLE__inputs_master />
          </div>
        </div>

        {render_column_right()}
      </div>
      <FOOTER__master />
    </div>
  );
}
